import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useMemo, useState } from 'react';
import { Group } from 'react-konva';
import { circleGroup } from '@/components/Board/Elements/CircleGroup';
import { DescriptionIcon } from '@/components/Board/Elements/DescriptionIcon';
import { imageGroup } from '@/components/Board/Elements/ImageGroup';
import { PresentationFrameLabel } from '@/components/Board/Elements/PresentationFrameLabel';
import { rectGroup } from '@/components/Board/Elements/RectGroup';
import { textGroup } from '@/components/Board/Elements/TextGroup';
import { getElementOffset, getNodePositionFromElement } from '@/utils/board/stageUtils';
import { isElementInViewport, isElementInViewportForCache } from '@/utils/board/viewportUtils';
/**
 * Мемоизированный компонент для отдельного элемента доски.
 * Перерендерится только если изменились его props или состояние выделения.
 */
const BoardElementComponent = memo(({ el, selectedIds, draftSelection, draggable, layer, disableImageCache, elementDragBoundFunc, onElementMouseDown, onElementMouseUp, onElementDragStart, onElementDragEnd, onElementDragMove, onElementDoubleClick, onDescriptionIconClick, zoom = 1, stagePosition, viewportSize, isPanning = false, isZooming = false, isTransforming = false, isMoving = false, isPresentationMode = false, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    const hasDescription = el.description && el.description.trim().length > 0;
    const elX = ((_a = el.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
    const elY = ((_b = el.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
    const elW = ((_c = el.props) === null || _c === void 0 ? void 0 : _c.w) || 0;
    const elH = ((_d = el.props) === null || _d === void 0 ? void 0 : _d.h) || 0;
    // Локальное состояние для hover (не влияет на перерендеры других компонентов)
    const [isHovered, setIsHovered] = useState(false);
    // Вычисляем размер иконки на основе размера элемента
    const iconSize = useMemo(() => {
        if (elW > 0 && elH > 0) {
            const minDimension = Math.min(elW, elH);
            const calculatedSize = Math.min(16, minDimension * 0.3);
            return Math.max(6, calculatedSize);
        }
        // Если размеры не указаны, используем базовый размер
        return 18;
    }, [elW, elH]);
    // ==================== ЛОГИКА РЕНДЕРИНГА ЭЛЕМЕНТА ====================
    // Перенесена из BoardElementRenderer.tsx
    const layerLocked = (layer === null || layer === void 0 ? void 0 : layer.locked) || false;
    const elementLocked = el.locked || false;
    const clip = (_e = el.props) === null || _e === void 0 ? void 0 : _e.clip; // {"type": "circle", "r": 30} | {"type": "rect", "x": 10, "y": 10, "width": 100, "height": 100}
    // Создаем Set только если массив не пустой, чтобы избежать лишних операций
    const draftSelectionSet = useMemo(() => (draftSelection.length > 0 ? new Set(draftSelection) : new Set()), [draftSelection]);
    const selectedIdsSet = useMemo(() => (selectedIds.length > 0 ? new Set(selectedIds) : new Set()), [selectedIds]);
    const isIntersecting = draftSelectionSet.has(el.id);
    const isSelectedInSet = selectedIdsSet.has(el.id);
    const showElementSelectionBorder = isIntersecting ||
        (selectedIds.length > 1 && isSelectedInSet) ||
        (isSelectedInSet && elementLocked);
    const elementX = ((_f = el.props) === null || _f === void 0 ? void 0 : _f.x) || 0;
    const elementY = ((_g = el.props) === null || _g === void 0 ? void 0 : _g.y) || 0;
    const elementWidth = ((_h = el.props) === null || _h === void 0 ? void 0 : _h.w) || 64;
    const elementHeight = ((_j = el.props) === null || _j === void 0 ? void 0 : _j.h) || 64;
    // Вычисляем offset для центрирования вращения вокруг центра элемента
    const { offsetX, offsetY } = useMemo(() => getElementOffset(elementWidth, elementHeight), [elementWidth, elementHeight]);
    // Корректируем позицию с учетом offset, чтобы элемент визуально остался на том же месте
    const { x: adjustedX, y: adjustedY } = useMemo(() => getNodePositionFromElement(elementX, elementY, offsetX, offsetY), [elementX, elementY, offsetX, offsetY]);
    const isDraggable = useMemo(() => {
        let result = draggable;
        if (el.isPresentationFrame) {
            if ((selectedIds.includes(el.id) || draftSelection.includes(el.id)) && !el.locked) {
                result = true;
            }
            else {
                result = false;
            }
        }
        return result;
    }, [draggable, el.isPresentationFrame, el.id, el.locked, selectedIds, draftSelection]);
    const isListening = useMemo(() => {
        let result = !el.isPresentationFrame;
        if (el.isPresentationFrame) {
            if ((selectedIds.includes(el.id) || draftSelection.includes(el.id)) && !el.locked) {
                result = true;
            }
            else {
                result = false;
            }
        }
        return result;
    }, [el.isPresentationFrame, el.id, el.locked, selectedIds, draftSelection]);
    const common = useMemo(() => {
        var _a;
        return ({
            id: `node-${el.id}`,
            name: 'element',
            x: adjustedX,
            y: adjustedY,
            offsetX,
            offsetY,
            rotation: ((_a = el.props) === null || _a === void 0 ? void 0 : _a.rotation) || 0,
            draggable: isDraggable,
            listening: isListening, // Делаем некликабельным для презентационных фреймов
            onMouseDown: (e) => onElementMouseDown === null || onElementMouseDown === void 0 ? void 0 : onElementMouseDown(e, el.id, el.locked, layerLocked),
            onMouseUp: (e) => onElementMouseUp === null || onElementMouseUp === void 0 ? void 0 : onElementMouseUp(e, el.id, el.locked, layerLocked),
            onDragStart: (e) => onElementDragStart === null || onElementDragStart === void 0 ? void 0 : onElementDragStart(el, e),
            onDragEnd: (e) => onElementDragEnd === null || onElementDragEnd === void 0 ? void 0 : onElementDragEnd(el, e),
            onDragMove: (e) => onElementDragMove === null || onElementDragMove === void 0 ? void 0 : onElementDragMove(el, e),
            dragBoundFunc: isDraggable
                ? (pos) => {
                    // Корректируем позицию с учетом offset при применении ограничений (снаппинг по сетке)
                    // pos - это позиция узла Konva в экранных координатах (с offset)
                    // elementDragBoundFunc теперь правильно обрабатывает offset внутри себя
                    return (elementDragBoundFunc === null || elementDragBoundFunc === void 0 ? void 0 : elementDragBoundFunc(pos, el.id)) || pos;
                }
                : undefined,
        });
    }, 
    // Используем только конкретные поля el (el.id, el.locked, el.props?.rotation)
    // вместо всего объекта el для оптимизации
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        el.id,
        el.locked,
        (_k = el.props) === null || _k === void 0 ? void 0 : _k.rotation,
        adjustedX,
        adjustedY,
        offsetX,
        offsetY,
        isDraggable,
        isListening,
        layerLocked,
        onElementMouseDown,
        onElementMouseUp,
        onElementDragStart,
        onElementDragEnd,
        onElementDragMove,
        elementDragBoundFunc,
    ]);
    const clipFunc = useMemo(() => (context) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        if (!clip)
            return;
        switch (clip.type) {
            case 'circle': {
                const r = (_a = clip.r) !== null && _a !== void 0 ? _a : Math.min(((_b = el.props) === null || _b === void 0 ? void 0 : _b.w) || 64, ((_c = el.props) === null || _c === void 0 ? void 0 : _c.h) || 64) / 2;
                context.arc((((_d = el.props) === null || _d === void 0 ? void 0 : _d.w) || 64) / 2, (((_e = el.props) === null || _e === void 0 ? void 0 : _e.h) || 64) / 2, r, 0, Math.PI * 2);
                break;
            }
            case 'rect': {
                const x = ((_g = (_f = el.props) === null || _f === void 0 ? void 0 : _f.clip) === null || _g === void 0 ? void 0 : _g.x) || 0;
                const y = ((_j = (_h = el.props) === null || _h === void 0 ? void 0 : _h.clip) === null || _j === void 0 ? void 0 : _j.y) || 0;
                const w = ((_l = (_k = el.props) === null || _k === void 0 ? void 0 : _k.clip) === null || _l === void 0 ? void 0 : _l.w) || ((_m = el.props) === null || _m === void 0 ? void 0 : _m.w) || 64;
                const h = ((_p = (_o = el.props) === null || _o === void 0 ? void 0 : _o.clip) === null || _p === void 0 ? void 0 : _p.h) || ((_q = el.props) === null || _q === void 0 ? void 0 : _q.h) || 64;
                context.rect(x, y, w, h);
                break;
            }
        }
    }, [clip, (_l = el.props) === null || _l === void 0 ? void 0 : _l.w, (_m = el.props) === null || _m === void 0 ? void 0 : _m.h, (_o = el.props) === null || _o === void 0 ? void 0 : _o.clip]);
    const getElementAbsoluteRect = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        // TODO: размеры с учетом маски (использовать для отрисовки элемента по clip?)
        const x = ((_a = el.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
        const y = ((_b = el.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
        const w = ((_c = el.props) === null || _c === void 0 ? void 0 : _c.w) || 0;
        const h = ((_d = el.props) === null || _d === void 0 ? void 0 : _d.h) || 0;
        const clip = (_e = el.props) === null || _e === void 0 ? void 0 : _e.clip;
        const absX = x + ((clip === null || clip === void 0 ? void 0 : clip.x) || 0);
        const absY = y + ((clip === null || clip === void 0 ? void 0 : clip.y) || 0);
        const absW = (_f = clip === null || clip === void 0 ? void 0 : clip.w) !== null && _f !== void 0 ? _f : w;
        const absH = (_g = clip === null || clip === void 0 ? void 0 : clip.h) !== null && _g !== void 0 ? _g : h;
        return { x: absX, y: absY, w: absW, h: absH };
    }, [el]);
    const boundBox = getElementAbsoluteRect;
    const originBox = useMemo(() => {
        var _a, _b;
        return ({
            x: elementX,
            y: elementY,
            w: ((_a = el.props) === null || _a === void 0 ? void 0 : _a.w) || 0,
            h: ((_b = el.props) === null || _b === void 0 ? void 0 : _b.h) || 0,
        });
    }, [elementX, elementY, (_p = el.props) === null || _p === void 0 ? void 0 : _p.w, (_q = el.props) === null || _q === void 0 ? void 0 : _q.h]);
    // Вычисляем видимость элемента для рендеринга (буфер = 1/3 ширины экрана)
    // ВАЖНО: видимость вычисляется только если есть информация о viewport
    // Во время панорамирования/зума viewport может быть неактуален, поэтому
    // по умолчанию считаем элемент видимым для предотвращения фриза
    const isVisibleForRender = useMemo(() => stagePosition && viewportSize
        ? isElementInViewport(el, stagePosition, zoom, viewportSize.width, viewportSize.height, viewportSize.width / 3)
        : true, // По умолчанию считаем видимым, если нет информации о viewport
    [el, stagePosition, zoom, viewportSize]);
    // Вычисляем видимость элемента для кэширования (буфер = 2/3 ширины экрана)
    // Используем расширенную зону для предкэширования элементов, которые скоро станут видимыми
    const isVisibleForCache = useMemo(() => stagePosition && viewportSize
        ? isElementInViewportForCache(el, stagePosition, zoom, viewportSize.width, viewportSize.height)
        : true, // По умолчанию считаем видимым для кэширования, если нет информации о viewport
    [el, stagePosition, zoom, viewportSize]);
    // Для рендеринга используем isVisibleForRender, для кэширования - isVisibleForCache
    const isVisible = isVisibleForRender;
    const renderElementParams = useMemo(() => ({
        el,
        showElementSelectionBorder,
        layerLocked,
        common,
        originBox,
        boundBox,
        clipFunc: clip ? clipFunc : undefined,
        zoom, // Передаем zoom для оптимизации кэширования изображений
        isVisible, // Передаем видимость для рендеринга
        isVisibleForCache, // Передаем видимость для кэширования (расширенная зона)
        isPresentationMode, // Передаем флаг режима презентации
    }), [
        el,
        showElementSelectionBorder,
        layerLocked,
        common,
        originBox,
        boundBox,
        clip,
        clipFunc,
        zoom,
        isVisible,
        isVisibleForCache,
        isPresentationMode,
    ]);
    // Рендерим элемент в зависимости от его типа
    const elementNode = useMemo(() => {
        var _a, _b;
        switch (el.elementType) {
            case 'shape': {
                if (((_a = el.props) === null || _a === void 0 ? void 0 : _a.shape) === 'rect') {
                    return rectGroup(renderElementParams);
                }
                if (((_b = el.props) === null || _b === void 0 ? void 0 : _b.shape) === 'circle') {
                    return circleGroup(renderElementParams);
                }
                return null;
            }
            case 'text': {
                return textGroup(Object.assign(Object.assign({}, renderElementParams), { onDoubleClick: onElementDoubleClick }));
            }
            case 'image':
            case 'token': {
                return imageGroup(Object.assign(Object.assign({}, renderElementParams), { onDoubleClick: onElementDoubleClick, 
                    // пробрасываем отключение кэша только для изображений
                    disableCache: !!disableImageCache, isPanning,
                    isZooming }));
            }
            default: {
                return null;
            }
        }
    }, [
        el.elementType,
        (_r = el.props) === null || _r === void 0 ? void 0 : _r.shape,
        renderElementParams,
        onElementDoubleClick,
        disableImageCache,
        isPanning,
        isZooming,
    ]);
    const shouldShowIcon = hasDescription && !isTransforming && !isMoving;
    // Всегда возвращаем Group для сохранения структуры DOM и предотвращения моргания
    return (_jsxs(Group, { onMouseEnter: () => {
            if (hasDescription && !isTransforming && !isMoving) {
                setIsHovered(true);
            }
        }, onMouseLeave: () => {
            setIsHovered(false);
        }, children: [elementNode, shouldShowIcon && !isPresentationMode && (_jsx(DescriptionIcon, { x: elX, y: elY, size: iconSize, visible: isHovered, onClick: (e) => {
                    e.cancelBubble = true; // Предотвращаем всплытие события
                    onDescriptionIconClick === null || onDescriptionIconClick === void 0 ? void 0 : onDescriptionIconClick(e, el.id);
                } })), el.isPresentationFrame && !isPresentationMode && (_jsx(PresentationFrameLabel, { x: adjustedX, y: adjustedY, offsetX: offsetX, offsetY: offsetY, rotation: ((_s = el.props) === null || _s === void 0 ? void 0 : _s.rotation) || 0, label: ((_t = el.name) === null || _t === void 0 ? void 0 : _t.trim()) || 'Presentation frame', elementId: el.id, visible: !isTransforming && !isMoving }))] }));
}, (prevProps, nextProps) => {
    var _a, _b, _c, _d;
    // Проверяем, изменился ли сам элемент (по хешу)
    if (prevProps.elementHash !== nextProps.elementHash) {
        return false; // Перерендерим - элемент изменился
    }
    // Проверяем, изменилось ли состояние выделения для этого элемента
    if (prevProps.isSelected !== nextProps.isSelected ||
        prevProps.isDraftSelected !== nextProps.isDraftSelected ||
        prevProps.showSelectionBorder !== nextProps.showSelectionBorder) {
        return false; // Перерендерим - изменилось выделение
    }
    // НЕ проверяем изменение draggable - оно обновляется напрямую через Konva API
    // для избежания перерендера при изменении режима
    // if (prevProps.draggable !== nextProps.draggable) {
    //   return false; // Перерендерим
    // }
    // Проверяем, изменился ли слой (например, locked состояние)
    if (((_a = prevProps.layer) === null || _a === void 0 ? void 0 : _a.locked) !== ((_b = nextProps.layer) === null || _b === void 0 ? void 0 : _b.locked) ||
        ((_c = prevProps.layer) === null || _c === void 0 ? void 0 : _c.id) !== ((_d = nextProps.layer) === null || _d === void 0 ? void 0 : _d.id)) {
        return false; // Перерендерим
    }
    // НЕ перерендериваем при изменении zoom/stagePosition/viewportSize/isPanning/isZooming
    // Эти изменения происходят при панорамировании/зуме и не требуют перерендеринга элементов
    // Видимость элементов обновляется асинхронно после завершения панорамирования/зума
    // isPanning/isZooming используются только внутри компонента для предотвращения очистки кэша
    // Это критично для предотвращения фриза во время панорамирования/зума
    // Если ничего не изменилось - не перерендериваем
    return true; // Не перерендерим
});
BoardElementComponent.displayName = 'BoardElement';
export default BoardElementComponent;
